// Multi-driver (team) helpers for the fuel-optimizer flow.
//
// A truck can be crewed by more than one driver. The driver-assignment API now
// returns every assigned driver in a `drivers` array (primary first), while
// still returning the single primary `driver` for backward compatibility. These
// helpers normalize that shape and fan a fuel-solution send out to the team.

/** A driver as returned by the assignment API - field names vary by source. */
interface RawDriver {
  driverID?: string | null;
  driver_id?: string | null;
  name?: string | null;
  driver_name?: string | null;
  phoneNumber?: string | null;
}

/** The driver-assignment API response shape consumed by extractDrivers. */
interface DriverAssignment {
  driver?: RawDriver | null;
  drivers?: RawDriver[];
}

/** A driver after normalization, used throughout the fanout. */
export interface Driver {
  driver_id: string | null;
  driver_name: string | null;
  phoneNumber: string | null;
}

/** A tractor object built by the form, carrying scalar driver fields or a drivers array. */
interface TractorDrivers {
  driver_id?: string | null;
  driver_name?: string | null;
  phoneNumber?: string | null;
  drivers?: Driver[];
}

/**
 * Normalize the driver-assignment API response into a flat driver list.
 * Prefers the multi-driver `drivers` array; falls back to the single `driver`
 * for older API responses. Entries without a driver id are dropped.
 *
 */
export function extractDrivers(driverAssignment: DriverAssignment | null): Driver[] {
  if (!driverAssignment) return [];

  const list = Array.isArray(driverAssignment.drivers) ? driverAssignment.drivers : [];
  const source = list.length > 0 ? list : driverAssignment.driver ? [driverAssignment.driver] : [];

  return source
    .filter((d) => d && (d.driverID ?? d.driver_id))
    .map((d) => ({
      driver_id: d.driverID ?? d.driver_id ?? null,
      driver_name: d.name ?? d.driver_name ?? null,
      phoneNumber: d.phoneNumber ?? null,
    }));
}

/**
 * The drivers carried on a tractor object built by the form. Uses the
 * `drivers` array when present, otherwise the scalar driver fields (so older
 * in-memory tractor objects still resolve to the single driver).
 */
export function tractorDrivers(tractor: TractorDrivers | null | undefined): Driver[] {
  if (!tractor) return [];
  if (Array.isArray(tractor.drivers) && tractor.drivers.length > 0) return tractor.drivers;
  if (!tractor.driver_id) return [];
  return [
    {
      driver_id: tractor.driver_id,
      driver_name: tractor.driver_name ?? null,
      phoneNumber: tractor.phoneNumber ?? null,
    },
  ];
}

/**
 * WhatsApp recipients: name + phone for each driver that has a phone number,
 * deduped by phone number.
 */
export function whatsAppRecipients(
  drivers: Pick<Driver, "driver_name" | "phoneNumber">[] | null | undefined
): { name: string | null; phoneNumber: string }[] {
  const seen = new Set<string>();
  const out: { name: string | null; phoneNumber: string }[] = [];
  for (const d of drivers ?? []) {
    if (!d.phoneNumber || seen.has(d.phoneNumber)) continue;
    seen.add(d.phoneNumber);
    out.push({ name: d.driver_name, phoneNumber: d.phoneNumber });
  }
  return out;
}

/**
 * Samsara / legacy recipients: deduped driver ids.
 */
export function driverIdRecipients(
  drivers: Pick<Driver, "driver_id">[] | null | undefined
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const d of drivers ?? []) {
    if (!d.driver_id || seen.has(d.driver_id)) continue;
    seen.add(d.driver_id);
    out.push(d.driver_id);
  }
  return out;
}
